import type { ToolCallApproval } from '@/components/assistant-ui/tool-status';
import { isCancelledBySend, type ToolGateKind } from '@/lib/constants/tool-outcomes';
import { createToolMatcher } from '@/lib/utils/tool-matcher';

import type { ToolCallMessagePart } from '@assistant-ui/react';

export function isTextPart(part: unknown): part is { type: 'text'; text: string } {
  return typeof part === 'object' && part !== null && (part as { type: string }).type === 'text';
}

export function isToolCallPart(part: unknown): part is ToolCallMessagePart {
  return (
    typeof part === 'object' &&
    part !== null &&
    'type' in part &&
    (part as { type: string }).type === 'tool-call'
  );
}

export function isRunning(part: { status?: { type: string } }): boolean {
  return part.status?.type === 'running';
}

// Mastra-internal tool whose calls are noise to the user — validator failures
// are recoverable (model retries with corrected args) and successful writes are
// background bookkeeping. Suppressed from the activity trail and the generic
// MessageError UI.
//
// Both spellings: the delivery mode decides which name Mastra gives it, and old threads keep the
// one they ran under.
const matchSuppressedTool = createToolMatcher(['updateWorkingMemory', 'setWorkingMemory']);
export function isSuppressedTool(toolName: string): boolean {
  return matchSuppressedTool(toolName);
}

/** The agent loading one of its procedures; `args.name` says which. */
export const SKILL_TOOL_NAME = 'skill';

export function isSkillCall(part: unknown): part is ToolCallMessagePart {
  return isToolCallPart(part) && part.toolName === SKILL_TOOL_NAME;
}

export function asksConfirmation(gate: ToolGateKind): boolean {
  return gate !== 'sign_in';
}

export function asksSignIn(gate: ToolGateKind): boolean {
  return gate !== 'confirm';
}

export interface ToolGate {
  gate: ToolGateKind;
  /** Set once the gate resolved: whether the guest was signed in when it did. */
  signedIn?: boolean;
  /** The guest's answer, when the call itself could not store it. */
  decision?: 'approved' | 'declined' | 'superseded';
}

/** A gated call's `artifact` in stored history. The back office builds it from the call's gate
 *  record as the DAL serves it, so only it has the sign-in attempts. */
export interface GateArtifact {
  toolGate: Omit<ToolGate, 'decision'>;
  signInAttempts?: string[];
}

export function gateArtifactOf({
  gate,
  signedIn,
  signInAttempts,
}: GateArtifact['toolGate'] & Pick<GateArtifact, 'signInAttempts'>): GateArtifact {
  return { toolGate: { gate, signedIn }, ...(signInAttempts && { signInAttempts }) };
}

export function toolGateOf(artifact: unknown): GateArtifact['toolGate'] | undefined {
  return (artifact as Partial<GateArtifact> | undefined)?.toolGate;
}

export function signInAttemptsOf(artifact: unknown): string[] {
  return (artifact as Partial<GateArtifact> | undefined)?.signInAttempts ?? [];
}

// Keep in lockstep with its three consumers: activity-trail, use-tool-group-state and
// should-render-message-error. Any string counts because Mastra persists a thrown tool call as
// `state: 'result'` whose result is the bare error message — no flag, no error shape.
export function isErrorResult(result: unknown): boolean {
  if (typeof result === 'string') return true;
  if (typeof result !== 'object' || result === null) return false;
  if ((result as { isError?: unknown }).isError === true) return true;
  // The shape both the live AI-SDK adapter and the platform history mapper give an output-error.
  return Boolean((result as { error?: unknown }).error);
}

/** Still able to produce a result: streaming, or paused on a call the user has yet to answer.
 *  assistant-ui marks a stopped message holding a result-less call `requires-action` with reason
 *  `tool-calls`; only `interrupt` means something still waits on the user. */
export function isMessageLive(status?: { type: string; reason?: string }): boolean {
  return (
    status?.type === 'running' ||
    (status?.type === 'requires-action' && status.reason === 'interrupt')
  );
}

/** An approved call whose message ended before its result came: it will never get one, and it
 *  may or may not have happened, so it is neither done nor failed.
 *
 *  Trap: with resumable streaming the server run can outlive the browser's message, so a tenant
 *  with both resumable streaming and approval gates would read "cut off" until the stream
 *  re-attaches. None has both today (T19's gate is a `human` tool, not an approval). */
export function wasCutOffAfterApproval({
  approval,
  result,
  messageLive,
}: {
  approval?: ToolCallApproval;
  result: unknown;
  messageLive: boolean;
}): boolean {
  return (
    !messageLive &&
    approval?.approved === true &&
    !approval.resolution &&
    (result === undefined || isCancelledBySend(result))
  );
}
